import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { countAdminBannerClick, getFlowBuilderBanner } from "../../services/app.service";
import { APP_PLATFORM } from "../../config";
import { Platform } from "../../models";

export interface BannerItem {
  id: string;
  name: string;
  status: string;
  title: string;
  description: string;
  buttonText: string;
  buttonUrl: string;
  bannerType: string;
  position: string;
  isDismissible: boolean;
  priority: number;
  displayRule: string;
  impressionsLimit: number | null;
  type: string;
  createdAt: string;
  updatedAt: string;
}

export interface BannerAnalyticsPayload {
  topic: "analytics.shopify.banner.impression" | "analytics.shopify.banner.click";
  data: Array<{
    date: number;
    value: number;
    type: "IMPRESSION" | "CLICK";
    bannerId: string;
    store: string;
    development: boolean;
  }>;
}

interface BannerState {
  isBannerLoading: boolean;
  flowBanners: BannerItem[];
  error?: string;
  slotStatuses?: any[] | null;
}

interface BannerClickPayload {
  item: any;
  shopName: string;
  isDismiss: boolean;
  isPartnerDevelopment: boolean;
}

const initialState: BannerState = {
  isBannerLoading: true,
  flowBanners: [],
  slotStatuses: [],
};

let bannersInitInFlight: Promise<{
  flowBanners: BannerItem[];
  isBannerLoading: boolean;
  error?: string;
}> | null = null;

export const initializeBanners = createAsyncThunk("banner/initializeBanners", async (_, { getState }) => {
  if (bannersInitInFlight) {
    return bannersInitInFlight;
  }

  bannersInitInFlight = (async () => {
  const state = getState() as RootState;
  const shopName = state.app.storeInfo?.storeUrl;

  try {
    if (APP_PLATFORM === Platform.Wix) {  
      return { flowBanners: [], isBannerLoading: false };
    }
    
    const flowBuilderBanners = await getFlowBuilderBanner();

    const flowBanners = (flowBuilderBanners || []).filter((currentBanner: BannerItem) => {
      const getBannersImp = localStorage.getItem(`bannerImp_${currentBanner?.id}_${shopName}`) || "0";
      const getBannersClick = localStorage.getItem(`bannerClick_${currentBanner?.id}_${shopName}`) || "0";

      if (currentBanner?.displayRule === "UNTIL_DISMISSED" && getBannersClick === "0") {
        localStorage.removeItem(`bannerImp_${currentBanner?.id}_${shopName}`);
        return true;
      } else if (currentBanner?.displayRule === "X_IMPRESSIONS_PER_MERCHANT" && currentBanner?.impressionsLimit && Number(getBannersImp) < currentBanner?.impressionsLimit) {
        localStorage.removeItem(`bannerClick_${currentBanner?.id}_${shopName}`);
        return true;
      } else if (currentBanner?.displayRule === "MANUAL") {
        return true;
      } else {
        return false;
      }
    });


     return { flowBanners, isBannerLoading: false };
  } catch (error) {
    console.error(error);
    return { isBannerLoading: false, error: "Failed to initialize banners." };
  }
  })().finally(() => {
    bannersInitInFlight = null;
  });

  return bannersInitInFlight;
});

const bannerSlice = createSlice({
  name: "banner",
  initialState,

  reducers: {
    bannerClick(state, action: PayloadAction<BannerClickPayload>) {
      const { item, shopName, isDismiss, isPartnerDevelopment } = action.payload;

      if (item.displayRule === "UNTIL_DISMISSED" || (item.displayRule === "X_IMPRESSIONS_PER_MERCHANT" && isDismiss)) {
        const getBannersClick = localStorage.getItem(`bannerClick_${item.id}_${shopName}`) || "0";

        localStorage.setItem(`bannerClick_${item.id}_${shopName}`, (Number(getBannersClick) + 1).toString());
      }

      if (!isDismiss) {
        const payload: BannerAnalyticsPayload = {
          topic: "analytics.shopify.banner.click",
          data: [
            {
              date: Date.now(),
              value: 1,
              type: "CLICK",
              bannerId: item.id,
              store: shopName,
              development: isPartnerDevelopment
            }
          ]
        };

        countAdminBannerClick(payload, shopName);
      }

      if (isDismiss) {
        if (item.position === "FLOW_BUILDER") {
          state.flowBanners = state.flowBanners.filter(a => a.id !== item.id);
        }
      }
    },
    setSlotStatuses(state, action: PayloadAction<any | null>) {
      state.slotStatuses = action.payload || [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(initializeBanners.pending, state => {
        state.isBannerLoading = true;
        state.error = undefined;
      })
      .addCase(initializeBanners.fulfilled, (state, action) => {
        state.isBannerLoading = action.payload.isBannerLoading;
        if (action.payload.error) {
          state.error = action.payload.error;
        } else {
          state.flowBanners = action.payload.flowBanners || [];
        }
      })
      .addCase(initializeBanners.rejected, (state, action) => {
        state.isBannerLoading = false;
        state.error = action.error.message;
      });
  },
});

export const { bannerClick, setSlotStatuses } = bannerSlice.actions;

export const selectBannerState = (state: RootState) => state.banner;

export default bannerSlice.reducer;
